/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// form validation schema
import { type } from '@testing-library/user-event/dist/type';
import * as yup from 'yup';

// Global Schema Components
// =========================================================

// Review Object
const ReviewSchema = yup.object().shape({
  development: yup.object().shape({
    status: yup.string().required('A status is required.'),
    approved: yup.boolean().required('An approval status is required.'),
    approval_data: yup.string()
  }),
  production: yup.object().shape({
    status: yup.string().required('A status is required.'),
    approved: yup.boolean().required('An approval status is required.'),
    approval_data: yup.string()
  })
});

const ReviewNotRequiredSchema = yup.object().shape({
  development: yup.object().shape({
    status: yup.string(),
    approved: yup.boolean(),
    approval_data: yup.string()
  }),
  production: yup.object().shape({
    status: yup.string(),
    approved: yup.boolean(),
    approval_data: yup.string()
  })
});

// Media Object
const MediaSchema = yup.object().shape({
  review: ReviewSchema,
  type: yup
    .string()
    .oneOf(
      ['image', 'audio', 'video', 'pdf'],
      'Type must be either an image, audio, or video'
    )
    .required('A media type is required.'),
  source_url: yup.string().required('Please add the link to source.'), // this should be changed to source url
  title_tl: yup.string().required('A Target Language title is required.'),
  title_en: yup.string().required('An English title is required.'),
  published_date: yup
    .string()
    .required('Please add the date media was published.'),
  downloaded_date: yup
    .string()
    .required('Please add the date media was downloaded/referenced.'),
  author: yup.string().required('Please add the author of the source.'),
  cc_license: yup.string().required('Please add the cc license of the source.'),
  media_url: yup.string(),

  // Conditional validation based on 'type' being image
  description: yup.string().when('type', (type, schema) => {
    return ['image'].includes(type)
      ? schema.required('Please add the description of the image')
      : schema.nullable();
  }),

  // Conditional validation based on 'type' being video or pdf
  // author_desc_tl: yup.string().when('type', (type, schema) => {
  //   return ['audio', 'video', 'pdf'].includes(type) ? schema.required("Please add the author's description in TL.") : schema.nullable();
  // }),
  // author_desc_en: yup.string().when('type', (type, schema) => {
  //   return ['audio', 'video', 'pdf'].includes(type) ? schema.required("Please add the author's description in EN.") : schema.nullable();
  // }),
  author_desc_tl: yup.string(),
  author_desc_en: yup.string(),

  // conditional validation based on 'type' being video
  // transcript: yup.string().when('type', (type, schema) => {
  //   return ['audio', 'video'].includes(type) ? schema.required("Please add the transcript.") : schema.nullable();
  // }),

  // conditional validation based on 'type' being pdf
  display_content: yup.string().when('type', (type, schema) => {
    return ['pdf'].includes(type)
      ? schema.required('Please add the displayed content of this reading.')
      : schema.nullable();
  })
});

const MediaNotRequiredSchema = yup.object().shape({
  review: ReviewSchema,
  type: yup
    .string()
    .oneOf(
      ['image', 'audio', 'video', 'pdf'],
      'Type must be either an image, audio, or video'
    ),
  source_url: yup.string(), // this should be changed to source url
  title_tl: yup.string(),
  title_en: yup.string(),
  published_date: yup.string(),
  downloaded_date: yup.string(),
  author: yup.string(),
  cc_license: yup.string(),
  media_url: yup.string(),
  description: yup.string(),
  author_desc_tl: yup.string(),
  author_desc_en: yup.string(),
  display_content: yup.string()
});

// Materials Object
const MaterialsSchema = yup.array().of(
  yup.object().shape({
    material_title_en: yup
      .string()
      .required('An english material title is required.'),
    material_title_tl: yup
      .string()
      .required('A target language material title name is required.'),
    source_url: yup.string().required('A source URL is required.'),
    media_url: yup.string().required('A media URL is required.'),
    media_material: yup.boolean(),
    media_page_id: yup
      .string()
      .required('A media page must be selected.')
      .test(
        'not-empty',
        'A media page must be selected.',
        (value) => value !== ''
      )
  })
);

// Developed Video Array
const DevelopedVideoSchema = yup.array().of(
  yup.object().shape({
    review: ReviewSchema,
    description: yup.string(),
    text_tl: yup
      .string()
      .required('A description of the narration in the TL is required.'),
    // text_en: yup.string().required("A description of the narration in English is required."),
    start_minute: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' ? null : value;
      })
      .nullable()
      .required('A start minute is required.')
      .test(
        'range',
        'Enter a number between 0 and 60.',
        (value) => value === null || (value >= 0 && value <= 60)
      ),
    start_second: yup
      .number()
      .required('A start second is required.')
      .min(0, 'Start second must be 0 or greater.')
      .max(60, 'Start second must be less than 60.'),
    end_minute: yup
      .number()
      .required('An end minute is required.')
      .min(0, 'End minute must be 0 or greater.')
      .max(60, 'End minute must be less than 60.'),
    end_second: yup
      .number()
      .required('An end second is required.')
      .min(0, 'End second must be 0 or greater.')
      .max(60, 'End second must be less than 60.')
  })
);

const IntroVideoSchema = yup.array().of(
  yup.object().shape({
    review: ReviewSchema,
    text_en: yup.string().required("A description of the narration in English is required."),
    start_minute: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' ? null : value;
      })
      .nullable()
      .required('A start minute is required.')
      .test(
        'range',
        'Enter a number between 0 and 60.',
        (value) => value === null || (value >= 0 && value <= 60)
      ),
    start_second: yup
      .number()
      .required('A start second is required.')
      .min(0, 'Start second must be 0 or greater.')
      .max(60, 'Start second must be less than 60.'),
    end_minute: yup
      .number()
      .required('An end minute is required.')
      .min(0, 'End minute must be 0 or greater.')
      .max(60, 'End minute must be less than 60.'),
    end_second: yup
      .number()
      .required('An end second is required.')
      .min(0, 'End second must be 0 or greater.')
      .max(60, 'End second must be less than 60.')
  })
);

// eMentor Array
const EMentorSchema = yup.array().of(
  yup.object().shape({
    review: ReviewSchema,
    title: yup.string().required('A title is required.'),
    content: yup.string().required('Content is required.'),
    source: yup.string().required('A source is required.')
  })
);

// Lesson Template Schemas
// =========================================================

// TMP-L-001
export const IntroductionVideoSchema = yup.object().shape({
  core: DevelopedVideoSchema,
  description: yup.string().required('A description of the video is required.'),
  transcript: yup.string().required('A transcript of the video is required.'),
  eMentor: EMentorSchema
});

// TMP-L-002
export const ObjectivesSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      tag: yup.string().required('A tag is required.'),
      media: MediaSchema,
      title_tl: yup.string().required('A title is required.'),
      desc_tl: yup.string().required('A description in the TL is required.'),
      title_en: yup.string().required('A title is required.'),
      desc_en: yup.string().required('A description in English is required.')
    })
  ),
  eMentor: EMentorSchema
});

// TMP-L-003
export const SceneSetterSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      media: MediaSchema,
      text_tl: yup
        .string()
        .required('A Target Language description is required.'),
      text_en: yup.string().required('An English description is required.')
    })
  ),
  eMentor: EMentorSchema
});

// TMP-L-004
export const TerminologyReviewSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      term_tl: yup.string().required('A term in the TL is required.'),
      term_en: yup.string().required('A term in English is required.'),
      definition: yup.string().required('A definition is required.'),
      synonym: yup.string(),
      antonym: yup.string(),
      sentence: yup.string(),
      pronunciation: yup.string()
    })
  ),
  eMentor: EMentorSchema
});

// TMP-L-005
export const ReadingSchema = yup.object().shape({
  media: MediaSchema,
  core: MediaSchema,
  focus_points: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      linked_item: yup.string().required('A linked item is required.'),
      focus_header: yup.string().required('A focus header is required.'),
      focus_content: yup.string().required('Focus content is required.')
    })
  ),
  eMentor: EMentorSchema
});

// TMP-L-006
export const ListeningSchema = yup.object().shape({
  media: MediaSchema,
  core: yup.object().shape({
    start_minute: yup
      .number()
      .required('A start minute is required.')
      .min(0, 'Start minute must be 0 or greater.')
      .max(60, 'Start minute must be less than 60.'),
    start_second: yup
      .number()
      .required('A start second is required.')
      .min(0, 'Start second must be 0 or greater.')
      .max(60, 'Start second must be less than 60.'),
    end_minute: yup
      .number()
      .required('An end minute is required.')
      .min(0, 'End minute must be 0 or greater.')
      .max(60, 'End minute must be less than 60.'),
    end_second: yup
      .number()
      .required('An end second is required.')
      .min(0, 'End second must be 0 or greater.')
      .max(60, 'End second must be less than 60.')
  }),
  cc: yup.array().of(
    yup.object().shape({
      start_minute: yup
        .number()
        .required('Start minute is required.')
        .min(0, 'Start minute must be greater than or equal to 0.')
        .max(60, 'Start minute must be less than or equal to 60.'),
      start_second: yup
        .number()
        .required('Start second is required.')
        .min(0, 'Start second must be greater than or equal to 0.')
        .max(60, 'Start second must be less than or equal to 60.'),
      end_minute: yup
        .number()
        .required('End minute is required.')
        .min(0, 'End minute must be greater than or equal to 0.')
        .max(60, 'End minute must be less than or equal to 60.'),
      end_second: yup
        .number()
        .required('End second is required.')
        .min(0, 'End second must be greater than or equal to 0.')
        .max(60, 'End second must be less than or equal to 60.'),
      tl_text: yup.string().required('A Target Language caption is required.')
    })
  ),
  focus_points: yup.array().of(
    yup.object().shape({
      start_minute: yup
        .number()
        .required('Start minute is required.')
        .min(0, 'Start minute must be greater than or equal to 0.')
        .max(60, 'Start minute must be less than or equal to 60.'),
      start_second: yup
        .number()
        .required('Start second is required.')
        .min(0, 'Start second must be greater than or equal to 0.')
        .max(60, 'Start second must be less than or equal to 60.'),
      end_minute: yup
        .number()
        .required('End minute is required.')
        .min(0, 'End minute must be greater than or equal to 0.')
        .max(60, 'End minute must be less than or equal to 60.'),
      end_second: yup
        .number()
        .required('End second is required.')
        .min(0, 'End second must be greater than or equal to 0.')
        .max(60, 'End second must be less than or equal to 60.'),
      title: yup.string().required('A title is required.'),
      content: yup.string().required('Content is required.')
    })
  ),
  questions: yup.array().of(
    yup.object().shape({
      start_minute: yup
        .number()
        .required('Start minute is required.')
        .min(0, 'Start minute must be greater than or equal to 0.')
        .max(60, 'Start minute must be less than or equal to 60.'),
      start_second: yup
        .number()
        .required('Start second is required.')
        .min(0, 'Start second must be greater than or equal to 0.')
        .max(60, 'Start second must be less than or equal to 60.'),
      end_minute: yup
        .number()
        .required('End minute is required.')
        .min(0, 'End minute must be greater than or equal to 0.')
        .max(60, 'End minute must be less than or equal to 60.'),
      end_second: yup
        .number()
        .required('End second is required.')
        .min(0, 'End second must be greater than or equal to 0.')
        .max(60, 'End second must be less than or equal to 60.'),
      title: yup.string().required('A title is required.'),
      template_id: yup
        .number()
        .transform((value, originalValue) =>
          originalValue === '' ? undefined : value
        )
        .nullable(true)
        .typeError('A template type is required.')
        .required('A template type is required.'),
      content: yup.object().shape({
        prompt: yup.string().required('A prompt is required.'),
        prompt_audio: yup.boolean(),
        lures: yup.array().of(
          yup.object().shape({
            lure_text: yup.string().required('A lure is required.'),
            use_audio: yup.boolean(),
            is_correct: yup.boolean()
          })
        ),
        correct_feedback: yup.string().when('template_id', {
          is: (template_id) => [0, 1, 2].includes(template_id),
          then: yup.string().required('Correct feedback is required.')
        }),
        incorrect_feedback: yup.string().when('template_id', {
          is: (template_id) => [0, 1, 2].includes(template_id),
          then: yup.string().required('Incorrect feedback is required.')
        }),
        suggested_feedback: yup.string().when('template_id', {
          is: (template_id) => template_id === 3,
          then: yup.string().required('Suggested feedback is required.')
        }),
        focus_point: yup.string().when('template_id', {
          is: (template_id) => template_id === 3,
          then: yup.string().required('A focus point is required.')
        })
      })
    })
  ),
  eMentor: yup.array().of(
    yup.object().shape({
      title: yup.string().required('A title is required.'),
      content: yup.string().required('Content is required.'),
      source: yup.string().required('A source is required.')
    })
  )
});

// TMP-L-007
export const ReadingComprehensionSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      type_id: yup.number().required('A type id is required.'),
      prompt: yup.string().required('A prompt is required.'),
      start_minute: yup.number().when('type_id', {
        is: (type_id) => [1, 3].includes(type_id),
        then: yup
          .number()
          .required('Start minute is required.')
          .min(0, 'Start minute must be greater than or equal to 0.')
          .max(60, 'Start minute must be less than or equal to 60.')
      }),
      start_second: yup.number().when('type_id', {
        is: (type_id) => [1, 3].includes(type_id),
        then: yup
          .number()
          .required('Start second is required.')
          .min(0, 'Start second must be greater than or equal to 0.')
          .max(60, 'Start second must be less than or equal to 60.')
      }),
      end_minute: yup.number().when('type_id', {
        is: (type_id) => [1, 3].includes(type_id),
        then: yup
          .number()
          .required('End minute is required.')
          .min(0, 'End minute must be greater than or equal to 0.')
          .max(60, 'End minute must be less than or equal to 60.')
      }),
      end_second: yup.number().when('type_id', {
        is: (type_id) => [1, 3].includes(type_id),
        then: yup
          .number()
          .required('End second is required.')
          .min(0, 'End second must be greater than or equal to 0.')
          .max(60, 'End second must be less than or equal to 60.')
      }),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          is_correct: yup.boolean()
        })
      ),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          start_minute: yup.number().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup
              .number()
              .required('Start minute is required.')
              .min(0, 'Start minute must be greater than or equal to 0.')
              .max(60, 'Start minute must be less than or equal to 60.')
          }),
          start_second: yup.number().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup
              .number()
              .required('Start second is required.')
              .min(0, 'Start second must be greater than or equal to 0.')
              .max(60, 'Start second must be less than or equal to 60.')
          }),
          end_minute: yup.number().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup
              .number()
              .required('End minute is required.')
              .min(0, 'End minute must be greater than or equal to 0.')
              .max(60, 'End minute must be less than or equal to 60.')
          }),
          end_second: yup.number().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup
              .number()
              .required('End second is required.')
              .min(0, 'End second must be greater than or equal to 0.')
              .max(60, 'End second must be less than or equal to 60.')
          }),
          is_correct: yup.boolean(),
          lure_feedback: yup.string().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup.string().required('Lure feedback is required.')
          })
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_3: yup
        .string()
        .required('Incorrect feedback is required.'),
      max_number_of_tries: yup
        .number()
        .required('A max number of tries is required.')
        .min(1, 'Max number of tries must be 0 or greater.')
        .max(3, 'Max number of tries must be less than 4.'),
      multi_select: yup.boolean()
    })
  ),
  eMentor: EMentorSchema
});

// TMP-L-008
export const ConceptCategorizingSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      type_id: yup.number().required('A type is required.'),
      prompt: yup.string().required('A prompt is required.'),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          lure_feedback: yup.string().required('A lure feedback is required.'),
          is_correct: yup.boolean()
        })
      )
    })
  ),
  eMentor: EMentorSchema
});

// TMP-L-009
export const ErrorIdentificationSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      start_minute: yup
        .number()
        .required('A start minute is required.')
        .min(0, 'Start minute must be 0 or greater.')
        .max(60, 'Start minute must be less than 60.'),
      start_second: yup
        .number()
        .required('A start second is required.')
        .min(0, 'Start second must be 0 or greater.')
        .max(60, 'Start second must be less than 60.'),
      end_minute: yup
        .number()
        .required('An end minute is required.')
        .min(0, 'End minute must be 0 or greater.')
        .max(60, 'End minute must be less than 60.'),
      end_second: yup
        .number()
        .required('An end second is required.')
        .min(0, 'End second must be 0 or greater.')
        .max(60, 'End second must be less than 60.'),
      authentic_transcript_correct: yup
        .string()
        .required('A correct authentic TL transcribed text is required.'),
      authentic_transcript_inaccurate: yup
        .string()
        .required('An inaccurate authentic TL transcribed text is required.'),
      lures: yup.array().of(
        yup.object().shape({
          error: yup.string().required('An error is required.'),
          feedback: yup.string().required('Feedback text is required.')
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_3: yup
        .string()
        .required('Incorrect feedback is required.'),
      max_number_of_tries: yup
        .number()
        .required('A max number of tries is required.')
        .min(1, 'Max number of tries must be 0 or greater.')
        .max(3, 'Max number of tries must be less than 4.')
    })
  ),
  eMentor: EMentorSchema
});

// TMP-L-010
export const SummaryEvaluationSchema = yup.object().shape({
  statements: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      prompt: yup.string().required('A prompt is required.'),
      option_1: yup.string().required('An option is required.'),
      option_2: yup.string().required('An option is required.'),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          correct_option: yup
            .number()
            .required('An authentic material number is required.')
            .min(1, 'Authentic material number must be 1 or 2.')
            .max(2, 'Authentic material number must be 1 or 2.')
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_3: yup
        .string()
        .required('Incorrect feedback is required.')
    })
  ),
  eMentor: EMentorSchema
});

// TMP-L-011
export const LinguisticEvaluationSchema = yup.object().shape({
  statements: yup.array().of(
    yup.object().shape({
      materials: MaterialsSchema,
      prompt: yup.string().required('A prompt is required.'),
      option_1: yup.string().required('An option is required.'),
      option_2: yup.string().required('An option is required.'),
      option_3: yup.string().required('An option is required.'),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          correct_option: yup
            .number()
            .required('An authentic material number is required.')
            .min(1, 'Authentic material number must be 1, 2, or 3.')
            .max(3, 'Authentic material number must be 1, 2, or 3.'),
          lure_correct_feedback: yup
            .string()
            .required('A correct feedback is required.'),
          lure_incorrect_feedback: yup
            .string()
            .required('An icorrect feedback is required.')
        })
      ),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.')
    })
  ),
  eMentor: yup.array().of(
    yup.object().shape({
      title: yup.string().required('A title is required.'),
      content: yup.string().required('Content is required.'),
      source: yup.string().required('A source is required.')
    })
  )
});

// TMP-L-012
export const RegisterAnalysisSchema = yup.object().shape({
  materials: MaterialsSchema,
  summary: yup.string().required('Summary is required.'),
  is_informal: yup.boolean(),
  statement_sets: yup.array().of(
    yup.object().shape({
      original: yup.string().required('Original is required.'),
      statements: yup.array().of(
        yup.object().shape({
          statement_text: yup.string().required('A statement is required.'),
          is_correct: yup.boolean(),
          type: yup.string().required('A statement is required.')
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_3: yup
        .string()
        .required('Incorrect feedback is required.'),
      max_number_of_tries: yup
        .number()
        .required('A max number of tries is required.')
        .min(1, 'Max number of tries must be 0 or greater.')
        .max(3, 'Max number of tries must be less than 4.')
    })
  ),
  eMentor: yup.array().of(
    yup.object().shape({
      title: yup.string().required('A title is required.'),
      content: yup.string().required('Content is required.'),
      source: yup.string().required('A source is required.')
    })
  )
});

// TMP-L-013
export const LessonSummarySchema = yup.object().shape({
  core: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    media: MediaSchema,
    label: yup.string().required('A label is required.'),
    text_en: yup.string().required('English Text is required.'),
    text_tl: yup.string().required('Target Language Text is required.'),
    no_jobaid_en: yup
      .string()
      .required('English No jobaid message is required.'),
    yes_jobaid_en: yup
      .string()
      .required('English Yes jobaid message is required.'),
    no_jobaid_tl: yup
      .string()
      .required('Target Language No jobaid message is required.'),
    yes_jobaid_tl: yup
      .string()
      .required('Target Language Yes jobaid message is required.'),
    pdf_text: yup.string(),
    jobaid_pdf_lead_tl: yup
      .string()
      .required('Target Language Jobaid lead text is required.'),
    jobaid_pdf_lead_en: yup
      .string()
      .required('English Jobaid lead text is required.')
  }),
  item_feedback: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      title_en: yup.string().required('English activity title is required.'),
      title_tl: yup
        .string()
        .required('Target Language activity title is required.'),
      interaction_type: yup
        .string()
        .required('An interaction type is required.'),
      interaction_id: yup
        .string()
        .required('A media page must be selected.')
        .test(
          'not-empty',
          'A media page must be selected.',
          (value) => value !== ''
        )
    })
  ),
  jobaid_pdf: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      title: yup
        .string()
        .required('A title for the authentic material summary is required.'),
      content_tl: yup
        .string()
        .required('Target Language statement content is required.'),
      content_en: yup
        .string()
        .required('English statement content is required.')
    })
  ),
  eMentor: EMentorSchema
});

// TMP-L-014
export const ModuleSummarySchema = yup.object().shape({
  core: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    media: MediaSchema,
    label: yup.string().required('A label is required.'),
    text_en: yup.string().required('English Text is required.'),
    text_tl: yup.string().required('Target Language Text is required.')
  }),
  item_feedback: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      title_en: yup.string().required('English activity title is required.'),
      title_tl: yup
        .string()
        .required('Target Language activity title is required.'),
      interaction_type: yup
        .string()
        .required('An interaction type is required.'),
      interaction_id: yup.string()
    })
  )
});

// TMP-L-015
export const EnrichmentSchema = yup.object().shape({
  core: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    media: MediaSchema,
    text: yup.string().required('A text description is required.'),
    link: yup.string().required('A link is required.'),
    conclusion: yup.string().required('A conclusion is required.')
  })
});

// Practical Exercise Template Schemas
// =========================================================

// TMP-PE-001
export const PETransitionSchema = yup.object().shape({});

// TMP-PE-002
export const MissionSelectionSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      difficulty: yup.string().required('A difficulty is required.'),
      media: MediaNotRequiredSchema,
      description: yup
        .string()
        .required('A difficulty description is required.')
    })
  )
});

// TMP-PE-003
export const PEBriefSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      difficulty: yup.string().required('A difficulty is required.'),
      description: yup
        .string()
        .required('A description of the video is required.'),
      transcript: yup
        .string()
        .required('A transcript of the video is required.'),
      segments: DevelopedVideoSchema
    })
  )
});

// TMP-PE-004
export const LPEEnvironmentSchema = yup.object().shape({
  environment: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    character: yup.string().required('A character is required.'),
    location: yup.string().required('A location is required.'),
    description: yup.string().required('A description is required.')
  }),
  scenario: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    text_tl: yup.string().required('A Target Language scenario is required.'),
    text_en: yup.string().required('An English scenario is required.')
  }),
  core: yup.array().of(
    yup.object().shape({
      object: yup.object().shape({
        id: yup.string().required('An id is required.'),
        label: yup.string().required('A label is required.'),
        device: yup.string().required('A device is required.')
      }),
      task: yup.object().shape({
        title_tl: yup.string().required('A Target Language title is required.'),
        title_en: yup.string().required('An English title is required.'),
        text_tl: yup
          .string()
          .required('A Target Language description is required.'),
        text_en: yup.string().required('An English description is required.'),
        type: yup.string().required('A task type is required.'),
        difficulty: yup.string().required('A difficulty is required.')
      }),
      content: yup.array().of(
        yup.object().shape({
          id: yup
            .string()
            .required('A page id is required.')
            .test(
              'not-empty',
              'A page must be selected.',
              (value) => value !== ''
            ),
          conditional: yup.boolean(),
          conditional_id: yup.string(),
          return: yup.boolean()
        })
      )
    })
  )
});

// TMP-PE-005
export const FPEEnvironmentHomeSchema = yup.object().shape({
  paths: yup.array().of(
    yup.object().shape({
      id: yup
        .string()
        .required('An id is required')
        .test('not-empty', 'A page must be selected.', (value) => value !== '')
    })
  )
});

// TMP-PE-006
export const FPEEnvironmentPathSchema = yup.object().shape({
  environment: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    character: yup.string().required('A character is required.'),
    location: yup.string().required('A location is required.'),
    description: yup.string().required('A description is required.')
  }),
  scenario: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    text_tl: yup.string().required('A Target Language scenario is required.'),
    text_en: yup.string().required('An English scenario is required.')
  }),
  immediate: yup.array().of(
    yup.object().shape({
      id: yup
        .string()
        .required('A page id is required.')
        .test('not-empty', 'A page must be selected.', (value) => value !== ''),
      conditional: yup.boolean(),
      conditional_id: yup.string(),
      return: yup.boolean(),
      next_id: yup.string()
    })
  ),
  core: yup.array().of(
    yup.object().shape({
      object: yup.object().shape({
        id: yup
          .string()
          .required('An id is required.')
          .test(
            'not-empty',
            'A page must be selected.',
            (value) => value !== ''
          ),
        label: yup.string().required('A label is required.'),
        device: yup.string().required('A device is required.')
      }),
      task: yup.object().shape({
        title_tl: yup.string().required('A Target Language title is required.'),
        title_en: yup.string().required('An English title is required.'),
        text_tl: yup
          .string()
          .required('A Target Language description is required.'),
        text_en: yup.string().required('An English description is required.'),
        type: yup.string().required('A task type is required.')
      }),
      content: yup.array().of(
        yup.object().shape({
          id: yup
            .string()
            .required('A page id is required.')
            .test(
              'not-empty',
              'A page must be selected.',
              (value) => value !== ''
            ),
          conditional: yup.boolean(),
          conditional_id: yup.string(),
          return: yup.boolean(),
          next_id: yup.string()
        })
      )
    })
  )
});

// TMP-PE-007
export const PEReadingAuthenticMaterialSchema = yup.object().shape({
  core: MediaSchema,
  focus_points: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      linked_item: yup.string().required('A linked item is required.'),
      focus_header: yup.string().required('A focus header is required.'),
      focus_content: yup.string().required('Focus content is required.')
    })
  ),
  eMentor: EMentorSchema
});

// TMP-PE-008
export const PEListeningAuthenticMaterialSchema = yup.object().shape({
  media: MediaSchema,
  core: yup.object().shape({
    start_minute: yup
      .number()
      .required('A start minute is required.')
      .min(0, 'Start minute must be 0 or greater.')
      .max(60, 'Start minute must be less than 60.'),
    start_second: yup
      .number()
      .required('A start second is required.')
      .min(0, 'Start second must be 0 or greater.')
      .max(60, 'Start second must be less than 60.'),
    end_minute: yup
      .number()
      .required('An end minute is required.')
      .min(0, 'End minute must be 0 or greater.')
      .max(60, 'End minute must be less than 60.'),
    end_second: yup
      .number()
      .required('An end second is required.')
      .min(0, 'End second must be 0 or greater.')
      .max(60, 'End second must be less than 60.')
  }),
  cc: yup.array().of(
    yup.object().shape({
      start_minute: yup
        .number()
        .required('Start minute is required.')
        .min(0, 'Start minute must be greater than or equal to 0.')
        .max(60, 'Start minute must be less than or equal to 60.'),
      start_second: yup
        .number()
        .required('Start second is required.')
        .min(0, 'Start second must be greater than or equal to 0.')
        .max(60, 'Start second must be less than or equal to 60.'),
      end_minute: yup
        .number()
        .required('End minute is required.')
        .min(0, 'End minute must be greater than or equal to 0.')
        .max(60, 'End minute must be less than or equal to 60.'),
      end_second: yup
        .number()
        .required('End second is required.')
        .min(0, 'End second must be greater than or equal to 0.')
        .max(60, 'End second must be less than or equal to 60.'),
      tl_text: yup.string().required('A Target Language caption is required.')
    })
  ),
  focus_points: yup.array().of(
    yup.object().shape({
      start_minute: yup
        .number()
        .required('Start minute is required.')
        .min(0, 'Start minute must be greater than or equal to 0.')
        .max(60, 'Start minute must be less than or equal to 60.'),
      start_second: yup
        .number()
        .required('Start second is required.')
        .min(0, 'Start second must be greater than or equal to 0.')
        .max(60, 'Start second must be less than or equal to 60.'),
      end_minute: yup
        .number()
        .required('End minute is required.')
        .min(0, 'End minute must be greater than or equal to 0.')
        .max(60, 'End minute must be less than or equal to 60.'),
      end_second: yup
        .number()
        .required('End second is required.')
        .min(0, 'End second must be greater than or equal to 0.')
        .max(60, 'End second must be less than or equal to 60.'),
      title: yup.string().required('A title is required.'),
      content: yup.string().required('Content is required.')
    })
  ),
  questions: yup.array().of(
    yup.object().shape({
      start_minute: yup
        .number()
        .required('Start minute is required.')
        .min(0, 'Start minute must be greater than or equal to 0.')
        .max(60, 'Start minute must be less than or equal to 60.'),
      start_second: yup
        .number()
        .required('Start second is required.')
        .min(0, 'Start second must be greater than or equal to 0.')
        .max(60, 'Start second must be less than or equal to 60.'),
      end_minute: yup
        .number()
        .required('End minute is required.')
        .min(0, 'End minute must be greater than or equal to 0.')
        .max(60, 'End minute must be less than or equal to 60.'),
      end_second: yup
        .number()
        .required('End second is required.')
        .min(0, 'End second must be greater than or equal to 0.')
        .max(60, 'End second must be less than or equal to 60.'),
      title: yup.string().required('A title is required.'),
      template_id: yup
        .number()
        .transform((value, originalValue) =>
          originalValue === '' ? undefined : value
        )
        .nullable(true)
        .typeError('A template type is required.')
        .required('A template type is required.'),
      content: yup.object().shape({
        prompt: yup.string().required('A prompt is required.'),
        prompt_audio: yup.boolean(),
        lures: yup.array().of(
          yup.object().shape({
            lure_text: yup.string().required('A lure is required.'),
            use_audio: yup.boolean(),
            is_correct: yup.boolean()
          })
        ),
        correct_feedback: yup.string().when('template_id', {
          is: (template_id) => [0, 1, 2].includes(template_id),
          then: yup.string().required('Correct feedback is required.')
        }),
        incorrect_feedback: yup.string().when('template_id', {
          is: (template_id) => [0, 1, 2].includes(template_id),
          then: yup.string().required('Incorrect feedback is required.')
        }),
        suggested_feedback: yup.string().when('template_id', {
          is: (template_id) => template_id === 3,
          then: yup.string().required('Suggested feedback is required.')
        }),
        focus_point: yup.string().when('template_id', {
          is: (template_id) => template_id === 3,
          then: yup.string().required('A focus point is required.')
        })
      })
    })
  ),
  eMentor: yup.array().of(
    yup.object().shape({
      title: yup.string().required('A title is required.'),
      content: yup.string().required('Content is required.'),
      source: yup.string().required('A source is required.')
    })
  )
});

// TMP-PE-009
export const PEReferenceReadingSchema = yup.object().shape({
  material: yup.string().required('A material is required.'),
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      prompt: yup.string().required('A prompt is required.'),
      start_paragraph: yup.number().required('Start minute is required.'),
      start_sentence: yup.number().required('Start second is required.'),
      end_paragraph: yup.number().required('End minute is required.'),
      end_sentence: yup.number().required('End second is required.'),
      feedback: yup.string().required('Feedback is required.')
    })
  )
});

// TMP-PE-010
export const PEReferenceListeningSchema = yup.object().shape({
  material: yup.string().required('A material is required.'),
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      prompt: yup.string().required('A prompt is required.'),
      start_minute: yup
        .number()
        .required('A start minute is required.')
        .min(0, 'Start minute must be 0 or greater.')
        .max(60, 'Start minute must be less than 60.'),
      start_second: yup
        .number()
        .required('A start second is required.')
        .min(0, 'Start second must be 0 or greater.')
        .max(60, 'Start second must be less than 60.'),
      end_minute: yup
        .number()
        .required('An end minute is required.')
        .min(0, 'End minute must be 0 or greater.')
        .max(60, 'End minute must be less than 60.'),
      end_second: yup
        .number()
        .required('An end second is required.')
        .min(0, 'End second must be 0 or greater.')
        .max(60, 'End second must be less than 60.'),
      feedback: yup.string().required('Feedback is required.')
    })
  )
});

// TMP-PE-011
export const PEMultipleChoiceSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      type_id: yup.number().required('A type id is required.'),
      prompt: yup.string().required('A prompt is required.'),
      start_minute: yup.number().when('type_id', {
        is: (type_id) => [1, 3].includes(type_id),
        then: yup
          .number()
          .required('Start minute is required.')
          .min(0, 'Start minute must be greater than or equal to 0.')
          .max(60, 'Start minute must be less than or equal to 60.')
      }),
      start_second: yup.number().when('type_id', {
        is: (type_id) => [1, 3].includes(type_id),
        then: yup
          .number()
          .required('Start second is required.')
          .min(0, 'Start second must be greater than or equal to 0.')
          .max(60, 'Start second must be less than or equal to 60.')
      }),
      end_minute: yup.number().when('type_id', {
        is: (type_id) => [1, 3].includes(type_id),
        then: yup
          .number()
          .required('End minute is required.')
          .min(0, 'End minute must be greater than or equal to 0.')
          .max(60, 'End minute must be less than or equal to 60.')
      }),
      end_second: yup.number().when('type_id', {
        is: (type_id) => [1, 3].includes(type_id),
        then: yup
          .number()
          .required('End second is required.')
          .min(0, 'End second must be greater than or equal to 0.')
          .max(60, 'End second must be less than or equal to 60.')
      }),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          is_correct: yup.boolean()
        })
      ),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          start_minute: yup.number().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup
              .number()
              .required('Start minute is required.')
              .min(0, 'Start minute must be greater than or equal to 0.')
              .max(60, 'Start minute must be less than or equal to 60.')
          }),
          start_second: yup.number().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup
              .number()
              .required('Start second is required.')
              .min(0, 'Start second must be greater than or equal to 0.')
              .max(60, 'Start second must be less than or equal to 60.')
          }),
          end_minute: yup.number().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup
              .number()
              .required('End minute is required.')
              .min(0, 'End minute must be greater than or equal to 0.')
              .max(60, 'End minute must be less than or equal to 60.')
          }),
          end_second: yup.number().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup
              .number()
              .required('End second is required.')
              .min(0, 'End second must be greater than or equal to 0.')
              .max(60, 'End second must be less than or equal to 60.')
          }),
          is_correct: yup.boolean(),
          lure_feedback: yup.string().when('type_id', {
            is: (type_id) => [1, 4].includes(type_id),
            then: yup.string().required('Lure feedback is required.')
          })
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      multi_select: yup.boolean()
    })
  ),
  eMentor: EMentorSchema
});

// TMP-PE-012
export const PEConceptComprehensionSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      type_id: yup.number().required('A type is required.'),
      prompt: yup.string().required('A prompt is required.'),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          lure_feedback: yup.string().required('A lure feedback is required.'),
          is_correct: yup.boolean()
        })
      )
    })
  ),
  eMentor: EMentorSchema
});

// TMP-PE-013
export const PESupplementalContentSchema = yup.object().shape({
  core: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    type_id: yup.number().required('A content type is required.'),
    media: yup.object().when('type_id', {
      is: (type_id) => [1, 2, 3, 4].includes(type_id),
      then: MediaSchema,
      otherwise: yup.object().notRequired() // No validation for other `type_id` values
    }),
    start_minute: yup.number().when('type_id', {
      is: (type_id) => [1, 2].includes(type_id),
      then: yup
        .number()
        .required('Start minute is required.')
        .min(0, 'Start minute must be greater than or equal to 0.')
        .max(60, 'Start minute must be less than or equal to 60.')
    }),
    start_second: yup.number().when('type_id', {
      is: (type_id) => [1, 2].includes(type_id),
      then: yup
        .number()
        .required('Start second is required.')
        .min(0, 'Start second must be greater than or equal to 0.')
        .max(60, 'Start second must be less than or equal to 60.')
    }),
    end_minute: yup.number().when('type_id', {
      is: (type_id) => [1, 2].includes(type_id),
      then: yup
        .number()
        .required('End minute is required.')
        .min(0, 'End minute must be greater than or equal to 0.')
        .max(60, 'End minute must be less than or equal to 60.')
    }),
    end_second: yup.number().when('type_id', {
      is: (type_id) => [1, 2].includes(type_id),
      then: yup
        .number()
        .required('End second is required.')
        .min(0, 'End second must be greater than or equal to 0.')
        .max(60, 'End second must be less than or equal to 60.')
    }),
    context: yup.string().when('type_id', {
      is: (type_id) => [1, 2, 3].includes(type_id),
      then: yup.string().required('Context is required.')
    }),
    prompt: yup.string().when('type_id', {
      is: (type_id) => [4].includes(type_id),
      then: yup.string().required('A prompt is required.')
    }),
    lures: yup
      .array()
      .nullable()
      .when('type_id', {
        is: (type_id) => [4].includes(type_id),
        then: yup.array().of(
          yup.object().shape({
            lure_text: yup.string().required('A lure is required.'),
            is_correct: yup.boolean()
          })
        ),
        otherwise: yup.array().notRequired() // No validation for other `type_id` values
      }),
    correct_feedback: yup.string().when('type_id', {
      is: (type_id) => [4].includes(type_id),
      then: yup.string().required('Correct feedback is required.')
    }),
    incorrect_feedback_1: yup.string().when('type_id', {
      is: (type_id) => [4].includes(type_id),
      then: yup.string().required('First incorrect feedback is required.')
    }),
    incorrect_feedback_2: yup.string().when('type_id', {
      is: (type_id) => [4].includes(type_id),
      then: yup.string().required('Second incorrect feedback is required.')
    }),
    incorrect_feedback_3: yup.string().when('type_id', {
      is: (type_id) => [4].includes(type_id),
      then: yup.string().required('Final incorrect feedback is required.')
    }),
    eMentor: yup
      .array()
      .nullable()
      .when('type_id', {
        is: (type_id) => [0].includes(type_id),
        then: yup.array().of(
          yup.object().shape({
            title: yup.string().required('A title is required.'),
            content: yup.string().required('Content is required.'),
            source: yup.string().required('A source is required.')
          })
        ),
        otherwise: yup.array().notRequired() // No validation for other `type_id` values
      })
  })
});

// TMP-PE-014
export const FPEInformativeEventSchema = yup.object().shape({
  core: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    type_id: yup.number().required('A content type is required.'),
    instructions: yup.string().required('Instructions are required.'),
    sender: yup.string().required('A sender is required.'),
    tl: yup.boolean().when('type_id', {
      is: (type_id) => [0, 2, 3].includes(type_id),
      then: yup.boolean().required('Language is required.')
    }),
    subject: yup.string().when('type_id', {
      is: (type_id) => [0, 2].includes(type_id),
      then: yup.string().required('A subject is required.')
    }),
    body: yup.string().when('type_id', {
      is: (type_id) => [0, 2, 3].includes(type_id),
      then: yup.string().required('Content is required.')
    }),
    transcript: yup.string().when('type_id', {
      is: (type_id) => [1].includes(type_id),
      then: yup.string().required('Transcript is required.')
    }),
    sender_desc: yup.string().when('type_id', {
      is: (type_id) => [4].includes(type_id),
      then: yup.string().required('Sender description is required.')
    }),
    statement: yup.string().when('type_id', {
      is: (type_id) => [4].includes(type_id),
      then: yup.string().required('A statement is required.')
    }),
    task_title: yup.string().required('A task title is required.')
  })
});

// TMP-PE-015
export const FPEDecisionEventSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      media: MediaSchema,
      title_tl: yup.string().required('A TL title is required.'),
      text_tl: yup.string().required('A TL description is required.'),
      title_en: yup.string().required('An English title is required.'),
      text_en: yup.string().required('An English description is required.'),
      progression_id: yup
        .string()
        .test('not-empty', 'A page must be selected.', (value) => value !== '')
    })
  )
});

// TMP-PE-016
export const FPEObservationEventSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      media: MediaSchema,
      text_tl: yup
        .string()
        .required('A Target Language description is required.'),
      text_en: yup.string().required('An English description is required.')
    })
  )
});

// TMP-PE-017
export const PEDialogInteractionSchema = yup.object().shape({
  dialog_segments: yup.array().of(
    yup.object().shape({
      id: yup.string(),
      review: ReviewNotRequiredSchema,
      initial_prompts: yup.array().of(
        yup.object().shape({
          user: yup.string().required('A user is required.'),
          use_audio: yup.boolean(),
          text: yup.string().required('Text is required.')
        })
      ),
      initial_responses: yup.array().of(
        yup.object().shape({
          use_audio: yup.boolean(),
          lure_text: yup.string().required('A lure text is required.'),
          is_correct: yup.boolean(),
          feedbacks: yup.array().of(
            yup.object().shape({
              user: yup.string().required('A user is required.'),
              use_audio: yup.boolean(),
              text: yup.string().required('Text is required.')
            })
          )
        })
      ),
      self_correction_prompts: yup.array().of(
        yup.object().shape({
          user: yup.string().required('A user is required.'),
          use_audio: yup.boolean(),
          text: yup.string().required('Text is required.')
        })
      ),
      self_correction_responses: yup.array().of(
        yup.object().shape({
          use_audio: yup.boolean(),
          lure_text: yup.string().required('A lure text is required.'),
          is_correct: yup.boolean(),
          feedbacks: yup.array().of(
            yup.object().shape({
              user: yup.string().required('A user is required.'),
              use_audio: yup.boolean(),
              text: yup.string().required('Text is required.')
            })
          )
        })
      )
    })
  )
});

// TMP-PE-018
export const PEPhoneCallInteractionSchema = yup.object().shape({
  character_name: yup.string().required('A character name is required.'),
  dialog_segments: yup.array().of(
    yup.object().shape({
      id: yup.string(),
      review: ReviewNotRequiredSchema,
      initial_prompts: yup.array().of(
        yup.object().shape({
          user: yup.string().required('A user is required.'),
          use_audio: yup.boolean(),
          text: yup.string().required('Text is required.')
        })
      ),
      initial_responses: yup.array().of(
        yup.object().shape({
          use_audio: yup.boolean(),
          lure_text: yup.string().required('A lure text is required.'),
          is_correct: yup.boolean(),
          feedbacks: yup.array().of(
            yup.object().shape({
              user: yup.string().required('A user is required.'),
              use_audio: yup.boolean(),
              text: yup.string().required('Text is required.')
            })
          )
        })
      ),
      self_correction_prompts: yup.array().of(
        yup.object().shape({
          user: yup.string().required('A user is required.'),
          use_audio: yup.boolean(),
          text: yup.string().required('Text is required.')
        })
      ),
      self_correction_responses: yup.array().of(
        yup.object().shape({
          use_audio: yup.boolean(),
          lure_text: yup.string().required('A lure text is required.'),
          is_correct: yup.boolean(),
          feedbacks: yup.array().of(
            yup.object().shape({
              user: yup.string().required('A user is required.'),
              use_audio: yup.boolean(),
              text: yup.string().required('Text is required.')
            })
          )
        })
      )
    })
  )
});

// TMP-PE-019
export const PEFaceToFaceInteractionSchema = yup.object().shape({
  env_desc: yup.object().shape({
    id: yup.string(),
    review: ReviewNotRequiredSchema,
    character_name: yup.string().required('A character name is required.'),
    character_desc: yup
      .string()
      .required('A character description is required.'),
    character_img_url: yup.string(),
    scene_desc: yup.string().required('A scene description is required.')
  }),
  dialog_segments: yup.array().of(
    yup.object().shape({
      id: yup.string(),
      review: ReviewNotRequiredSchema,
      initial_prompts: yup.array().of(
        yup.object().shape({
          user: yup.string().required('A user is required.'),
          use_audio: yup.boolean(),
          text: yup.string().required('Text is required.'),
          cc: yup.array().of(
            yup.object().shape({
              start_minute: yup
                .number()
                .required('Start minute is required.')
                .min(0, 'Start minute must be greater than or equal to 0.')
                .max(60, 'Start minute must be less than or equal to 60.'),
              start_second: yup
                .number()
                .required('Start second is required.')
                .min(0, 'Start second must be greater than or equal to 0.')
                .max(60, 'Start second must be less than or equal to 60.'),
              end_minute: yup
                .number()
                .required('End minute is required.')
                .min(0, 'End minute must be greater than or equal to 0.')
                .max(60, 'End minute must be less than or equal to 60.'),
              end_second: yup
                .number()
                .required('End second is required.')
                .min(0, 'End second must be greater than or equal to 0.')
                .max(60, 'End second must be less than or equal to 60.'),
              tl_text: yup.string().required('A Target Language caption is required.')
            })
          )
        })
      ),
      initial_responses: yup.array().of(
        yup.object().shape({
          use_audio: yup.boolean(),
          lure_text: yup.string().required('A lure text is required.'),
          is_correct: yup.boolean(),
          feedbacks: yup.array().of(
            yup.object().shape({
              user: yup.string().required('A user is required.'),
              use_audio: yup.boolean(),
              text: yup.string().required('Text is required.'),
              cc: yup.array().of(
                yup.object().shape({
                  start_minute: yup
                    .number()
                    .required('Start minute is required.')
                    .min(0, 'Start minute must be greater than or equal to 0.')
                    .max(60, 'Start minute must be less than or equal to 60.'),
                  start_second: yup
                    .number()
                    .required('Start second is required.')
                    .min(0, 'Start second must be greater than or equal to 0.')
                    .max(60, 'Start second must be less than or equal to 60.'),
                  end_minute: yup
                    .number()
                    .required('End minute is required.')
                    .min(0, 'End minute must be greater than or equal to 0.')
                    .max(60, 'End minute must be less than or equal to 60.'),
                  end_second: yup
                    .number()
                    .required('End second is required.')
                    .min(0, 'End second must be greater than or equal to 0.')
                    .max(60, 'End second must be less than or equal to 60.'),
                  tl_text: yup.string().required('A Target Language caption is required.')
                })
              )
            })
          )
        })
      ),
      self_correction_prompts: yup.array().of(
        yup.object().shape({
          user: yup.string().required('A user is required.'),
          use_audio: yup.boolean(),
          text: yup.string().required('Text is required.'),
          cc: yup.array().of(
            yup.object().shape({
              start_minute: yup
                .number()
                .required('Start minute is required.')
                .min(0, 'Start minute must be greater than or equal to 0.')
                .max(60, 'Start minute must be less than or equal to 60.'),
              start_second: yup
                .number()
                .required('Start second is required.')
                .min(0, 'Start second must be greater than or equal to 0.')
                .max(60, 'Start second must be less than or equal to 60.'),
              end_minute: yup
                .number()
                .required('End minute is required.')
                .min(0, 'End minute must be greater than or equal to 0.')
                .max(60, 'End minute must be less than or equal to 60.'),
              end_second: yup
                .number()
                .required('End second is required.')
                .min(0, 'End second must be greater than or equal to 0.')
                .max(60, 'End second must be less than or equal to 60.'),
              tl_text: yup.string().required('A Target Language caption is required.')
            })
          )
        })
      ),
      self_correction_responses: yup.array().of(
        yup.object().shape({
          use_audio: yup.boolean(),
          lure_text: yup.string().required('A lure text is required.'),
          is_correct: yup.boolean(),
          feedbacks: yup.array().of(
            yup.object().shape({
              user: yup.string().required('A user is required.'),
              use_audio: yup.boolean(),
              text: yup.string().required('Text is required.'),
              cc: yup.array().of(
                yup.object().shape({
                  start_minute: yup
                    .number()
                    .required('Start minute is required.')
                    .min(0, 'Start minute must be greater than or equal to 0.')
                    .max(60, 'Start minute must be less than or equal to 60.'),
                  start_second: yup
                    .number()
                    .required('Start second is required.')
                    .min(0, 'Start second must be greater than or equal to 0.')
                    .max(60, 'Start second must be less than or equal to 60.'),
                  end_minute: yup
                    .number()
                    .required('End minute is required.')
                    .min(0, 'End minute must be greater than or equal to 0.')
                    .max(60, 'End minute must be less than or equal to 60.'),
                  end_second: yup
                    .number()
                    .required('End second is required.')
                    .min(0, 'End second must be greater than or equal to 0.')
                    .max(60, 'End second must be less than or equal to 60.'),
                  tl_text: yup.string().required('A Target Language caption is required.')
                })
              )
            })
          )
        })
      )
    })
  )
});

// TMP-PE-020
export const PEFeedbackEventSchema = yup.object().shape({
  core: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    text_tl: yup
      .string()
      .required('A Target Language summary point is required.'),
    text_en: yup.string().required('An English summary point is required.'),
    unlock_id: yup.number().required('An unlocked item is required.')
  })
});

// TMP-PE-021
export const PEConstructedResponseSchema = yup.object().shape({
  core: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    prompt: yup.string().required('A prompt is required.'),
    response: yup.string().required('A response is required.')
  }),
  eMentor: yup.array().of(
    yup.object().shape({
      title: yup.string().required('A title is required.'),
      content: yup.string().required('Content is required.'),
      source: yup.string().required('A source is required.')
    })
  )
});

// TMP-PE-022
export const PEConstructedDraftReportSchema = yup.object().shape({
  prompt_instructions_tl: yup
    .string()
    .required('Instructions for the prompt are required.'),
  prompt_instructions_en: yup
    .string()
    .required('Instructions for the prompt are required.'),
  prompt: yup.string().required('A prompt is required.'),
  summary_prompt_en: yup.string().required('A summary is required'),
  response: yup.string().required('A response is required.')
});

// TMP-PE-023
export const PEHypothesisResponseSchema = yup.object().shape({
  prompt: yup.string().required('A prompt is required.'),
  response: yup.string().required('A response is required.')
});

// TMP-PE-024
export const PEPresentationBriefSchema = yup.object().shape({
  ppt_title: yup.string().required('A title is required.'),
  media: MediaSchema,
  prompt_tl: yup.string().required('A TL prompt is required.'),
  prompt_en: yup.string().required('An English prompt is required.'),
  key_points_tl: yup.string().required('TL key points are required.'),
  key_points_en: yup.string().required('English key points are required.'),
  sa_ins_tl: yup.string().required('A TL schoolhouse instruction is required.'),
  sa_ins_en: yup
    .string()
    .required('An English schoolhouse instruction is required.'),
  response: yup.string().required('A response is required.')
});

// TMP-PE-025
export const PEArgumentCounterArgumentSchema = yup.object().shape({
  prompt: yup.string().required('A prompt is required.'),
  npc: yup.object().shape({
    name: yup.string().required('A name is required.'),
    title: yup.string().required('A title is required.')
  }),
  core: DevelopedVideoSchema,
  response: yup.string().required('A response is required.')
});

// TMP-PE-026
export const PEWhitePaperSchema = yup.object().shape({
  prompt_instructions_tl: yup
    .string()
    .required('Instructions for the prompt are required.'),
  prompt_instructions_en: yup
    .string()
    .required('Instructions for the prompt are required.'),
  prompt: yup.string().required('A prompt is required.'),
  summary_prompt_en: yup.string().required('A summary is required'),
  response: yup.string().required('A response is required.')
});

// TMP-PE-027
export const PENoteCardsSchema = yup.object().shape({
  context_tl: yup.string().required('A contect_tl is required.'),
  context_en: yup.string().required('A context_en is required.'),
  prompt: yup.string().required('A prompt is required.'),
  cards: yup.array().of(
    yup.object().shape({
      title: yup.string().required('A title is required.'),
      response: yup.string().required('Content is required.')
    })
  ),
  eMentor: yup.array().of(
    yup.object().shape({
      title: yup.string().required('A title is required.'),
      content: yup.string().required('Content is required.'),
      source: yup.string().required('A source is required.')
    })
  )
});

// TMP-PE-028
export const PEOpEdPaperSchema = yup.object().shape({
  prompt_instructions_tl: yup
    .string()
    .required('Instructions for the prompt are required.'),
  prompt_instructions_en: yup
    .string()
    .required('Instructions for the prompt are required.'),
  prompt: yup.string().required('A prompt is required.'),
  summary_prompt_en: yup.string().required('A summary is required'),
  response: yup.string().required('A response is required.'),
  title: yup.string().required('A title is required.'),
  subtitle1: yup.string().required('A subtitle is required.'),
  subtitle2: yup.string(),
  send_email: yup.boolean(),
  email_destination: yup.string().when('send_email', {
    is: true,
    then: yup.string().required('An email destination is required.')
  }),
  email_subject: yup.string().when('send_email', {
    is: true,
    then: yup.string().required('An email destination is required.')
  }),
  materials: yup.array().of(
    yup.object().shape({
      id: yup.string().required('A material page id is required.')
    })
  )
});

// TMP-PE-029
export const FPECapstoneReportSchema = yup.object().shape({
  core: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    prompt_tl: yup.string().required('A TL prompt is required.'),
    prompt_en: yup.string().required('An English prompt is required.'),
    response: yup.string().required('A response is required.')
  }),
  section: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    prompt: yup.string().required('A TL prompt is required.'),
    lures: yup.array().of(
      yup.object().shape({
        lure_text: yup.string().required('A lure is required.'),
        is_correct: yup.boolean()
      })
    )
  }),
  notebook: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      interaction_id: yup
        .string()
        .required('A page must be selected.')
        .test('not-empty', 'A page must be selected.', (value) => value !== '')
    })
  )
});

// TMP-PE-030
export const PEDebriefSchema = yup.object().shape({
  core: yup.object().shape({
    id: yup.string().required('An id is required.'),
    review: ReviewSchema,
    media: MediaSchema,
    label: yup.string().required('A label is required.'),
    text_tl: yup
      .string()
      .required('A Target Language description is required.'),
    text_en: yup.string().required('An English description is required.'),
    environment: yup
      .string()
      .required('An environment is required.')
      .test('not-empty', 'A page must be selected.', (value) => value !== ''),
    fpe: yup.boolean(),
    env_path_1: yup.string().when('fpe', {
      is: true,
      then: yup
        .string()
        .required('A page must be selected.')
        .test('not-empty', 'A page must be selected.', (value) => value !== ''),
      otherwise: yup.string().nullable()
    }),
    env_path_2: yup.string().when('fpe', {
      is: true,
      then: yup
        .string()
        .required('A page must be selected.')
        .test('not-empty', 'A page must be selected.', (value) => value !== ''),
      otherwise: yup.string().nullable()
    }),
    env_path_3: yup.string(),
    no_jobaid_en: yup.string(),
    yes_jobaid_en: yup.string(),
    no_jobaid_tl: yup.string(),
    yes_jobaid_tl: yup.string(),
    pdf_text: yup.string(),
    jobaid_pdf_lead_tl: yup.string(),
    jobaid_pdf_lead_en: yup.string()
  }),
  item_feedback: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      title_en: yup.string().required('English activity title is required.'),
      title_tl: yup
        .string()
        .required('Target Language activity title is required.'),
      interaction_type: yup
        .string()
        .required('An interaction type is required.'),
      interaction_id: yup
        .string()
        .required('A page must be selected.')
        .test('not-empty', 'A page must be selected.', (value) => value !== '')
    })
  ),
  jobaid_pdf: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      title: yup
        .string()
        .required('A title for the authentic material summary is required.'),
      content_tl: yup
        .string()
        .required('Target Language statement content is required.'),
      content_en: yup
        .string()
        .required('English statement content is required.')
    })
  )
});

// Assessment Template Schemas
// =========================================================

// TMP-A-001
export const AssessmentIntroductionScreenSchema = yup.object().shape({
  media: MediaSchema,
  en_introduction: yup
    .string()
    .required('An English introduction is required.'),
  tl_introduction: yup
    .string()
    .required('A target language introduction is required.')
});

// TMP-A-002
export const AssessmentReadingMultipleChoiceSchema = yup.object().shape({
  reading_title_en: yup
    .string()
    .required('An english reading title is required.'),
  reading_title_tl: yup
    .string()
    .required('A target language reading title name is required.'),
  source_url: yup.string().required('A link is required.'),
  material_location: yup
    .string()
    .notOneOf(['none'], 'Select a valid location')
    .required(),
  media_url: yup.string(),
  media_page_id: yup
    .string()
    .required('A media page must be selected.')
    .test(
      'not-empty',
      'A media page must be selected.',
      (value) => value !== ''
    ),
  question: yup.object().shape({
    para_num: yup.string().required('A paragraph number is required.'),
    pdf_page_num: yup.number(),
    prompt: yup.string().required('A prompt is required.'),
    lures: yup.array().of(
      yup.object().shape({
        lure_text: yup.string().required('A lure text is required.'),
        is_correct: yup.boolean()
      })
    ),
    multi_select: yup.boolean()
  })
});

// TMP-A-003
export const AssessmentListeningMultipleChoiceSchema = yup.object().shape({
  media_title_en: yup.string().required('An english media title is required.'),
  media_title_tl: yup
    .string()
    .required('A target language media title name is required.'),
  source_url: yup.string().required('A link is required.'),
  material_location: yup
    .string()
    .notOneOf(['none'], 'Select a valid location')
    .required(),
  media_url: yup.string(),
  media_page_id: yup
    .string()
    .required('A media page must be selected.')
    .test(
      'not-empty',
      'A media page must be selected.',
      (value) => value !== ''
    ),
  question: yup.object().shape({
    prompt: yup.string().required('A prompt is required.'),
    start_minute: yup
      .number()
      .required('A start minute is required.')
      .min(0, 'Start minute must be 0 or greater.')
      .max(60, 'Start minute must be less than 60.'),
    start_second: yup
      .number()
      .required('A start second is required.')
      .min(0, 'Start second must be 0 or greater.')
      .max(60, 'Start second must be less than 60.'),
    end_minute: yup
      .number()
      .required('An end minute is required.')
      .min(0, 'End minute must be 0 or greater.')
      .max(60, 'End minute must be less than 60.'),
    end_second: yup
      .number()
      .required('An end second is required.')
      .min(0, 'End second must be 0 or greater.')
      .max(60, 'End second must be less than 60.'),
    lures: yup.array().of(
      yup.object().shape({
        lure_text: yup.string().required('A lure text is required.'),
        is_correct: yup.boolean()
      })
    ),
    multi_select: yup.boolean()
  })
});

// TMP-A-001
export const AssessmentSummaryScreenSchema = yup.object().shape({
  media: MediaSchema,
  min_score: yup
    .number()
    .required('A minimum score is required.')
    .min(0, 'Minimum score must be 0 or greater.')
    .max(100, 'Minimum score must be less than or equal to 100.'),
  tl_prefix: yup.string().required('Please add the prefix in TL.'),
  tl_passed: yup.string().required('Please add the passing additional in TL.'),
  tl_failed: yup.string().required('Please add the failure additional in TL.'),
  en_prefix: yup.string().required('Please add the prefix in EN.'),
  en_passed: yup.string().required('Please add the passing additional in EN.'),
  en_failed: yup.string().required('Please add the failure additional in EN.')
});

// Introduction Template Schemas
// =========================================================

// TMP-I-001
export const IntroCarouselSchema = yup.object().shape({
  core: yup.array().of(
    yup.object().shape({
      media: MediaSchema,
      title: yup.string().required('A title is required.'),
      description: yup.string().required('A description is required.')
    })
  )
});

// TMP-I-002
export const TutorialVideoSchema = yup.object().shape({
  core: IntroVideoSchema,
  transcript: yup.string().required('A transcript of the video is required.'),
});


// TMP-I-003
export const IntroSummarySchema = yup.object().shape({
  media: MediaSchema,
  text: yup.string().required('A text description is required.')
});

// Old Templates to be removed
// =========================================================

export const DiscussionQuestionSchema = yup.object().shape({
  summary: yup.string().required('A summary is required.'),
  scenario_tl: yup.string().required('A scenario is required.'),
  schoolhouse_responses: yup.array().of(
    yup.object().shape({
      response_sections: yup.array().of(
        yup.object().shape({
          text: yup.string().required('A section is required.')
        })
      )
    })
  ),
  eMentor: yup.array().of(
    yup.object().shape({
      title: yup.string().required('A title is required.'),
      content: yup.string().required('Content is required.'),
      source: yup.string().required('A source is required.')
    })
  )
});

export const QuestionSelectionSchema = yup.object().shape({
  questions: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      prompt: yup.string().required('A prompt is required.'),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          is_correct: yup.boolean()
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_3: yup
        .string()
        .required('Incorrect feedback is required.'),
      max_number_of_tries: yup
        .number()
        .required('A max number of tries is required.')
        .min(1, 'Max number of tries must be 0 or greater.')
        .max(3, 'Max number of tries must be less than 4.'),
      multi_select: yup.boolean()
    })
  ),
  eMentor: EMentorSchema
});

export const StatementCompletionSchema = yup.object().shape({
  questions: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      prompt: yup.string().required('A prompt is required.'),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          is_correct: yup.boolean(),
          lure_feedback: yup.string().required('A lure feedback is required.')
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_3: yup
        .string()
        .required('Incorrect feedback is required.'),
      max_number_of_tries: yup
        .number()
        .required('A max number of tries is required.')
        .min(1, 'Max number of tries must be 0 or greater.')
        .max(3, 'Max number of tries must be less than 4.'),
      multi_select: yup.boolean()
    })
  ),
  eMentor: EMentorSchema
});

export const SkillIntegrationMultipleChoiceSchema = yup.object().shape({
  questions: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      question_text: yup.string().required('A question is required.'),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          start_minute: yup
            .number()
            .required('A start minute is required.')
            .min(0, 'Start minute must be 0 or greater.')
            .max(60, 'Start minute must be less than 60.'),
          start_second: yup
            .number()
            .required('A start second is required.')
            .min(0, 'Start second must be 0 or greater.')
            .max(60, 'Start second must be less than 60.'),
          end_minute: yup
            .number()
            .required('An end minute is required.')
            .min(0, 'End minute must be 0 or greater.')
            .max(60, 'End minute must be less than 60.'),
          end_second: yup
            .number()
            .required('An end second is required.')
            .min(0, 'End second must be 0 or greater.')
            .max(60, 'End second must be less than 60.'),
          is_correct: yup.boolean(),
          lure_feedback: yup.string().required('A lure feedback is required.')
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_3: yup
        .string()
        .required('Incorrect feedback is required.'),
      max_number_of_tries: yup
        .number()
        .required('A max number of tries is required.')
        .min(1, 'Max number of tries must be 0 or greater.')
        .max(3, 'Max number of tries must be less than 4.'),
      multi_select: yup.boolean()
    })
  ),
  eMentor: EMentorSchema
});

export const ListeningComprehensionSchema = yup.object().shape({
  questions: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      question_text: yup.string().required('A question is required.'),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          is_correct: yup.boolean()
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_3: yup
        .string()
        .required('Incorrect feedback is required.'),
      max_number_of_tries: yup
        .number()
        .required('A max number of tries is required.')
        .min(1, 'Max number of tries must be 0 or greater.')
        .max(3, 'Max number of tries must be less than 4.'),
      multi_select: yup.boolean()
    })
  ),
  eMentor: EMentorSchema
});

export const TranslationAnalysisSchema = yup.object().shape({
  questions: yup.array().of(
    yup.object().shape({
      id: yup.string().required('An id is required.'),
      review: ReviewSchema,
      materials: MaterialsSchema,
      prompt: yup.string().required('A prompt is required.'),
      start_minute: yup
        .number()
        .required('A start minute is required.')
        .min(0, 'Start minute must be 0 or greater.')
        .max(60, 'Start minute must be less than 60.'),
      start_second: yup
        .number()
        .required('A start second is required.')
        .min(0, 'Start second must be 0 or greater.')
        .max(60, 'Start second must be less than 60.'),
      end_minute: yup
        .number()
        .required('An end minute is required.')
        .min(0, 'End minute must be 0 or greater.')
        .max(60, 'End minute must be less than 60.'),
      end_second: yup
        .number()
        .required('An end second is required.')
        .min(0, 'End second must be 0 or greater.')
        .max(60, 'End second must be less than 60.'),
      lures: yup.array().of(
        yup.object().shape({
          lure_text: yup.string().required('A lure is required.'),
          is_correct: yup.boolean()
        })
      ),
      correct_feedback: yup.string().required('Correct feedback is required.'),
      incorrect_feedback_1: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_2: yup
        .string()
        .required('Incorrect feedback is required.'),
      incorrect_feedback_3: yup
        .string()
        .required('Incorrect feedback is required.'),
      max_number_of_tries: yup
        .number()
        .required('A max number of tries is required.')
        .min(1, 'Max number of tries must be 0 or greater.')
        .max(3, 'Max number of tries must be less than 4.'),
      multi_select: yup.boolean()
    })
  ),
  eMentor: EMentorSchema
});

export const PECommunicationIntroductionSchema = yup.object().shape({
  video_segments: yup.array().of(
    yup.object().shape({
      segment_description: yup
        .string()
        .required('A segment description is required.'),
      segment_narration_tl: yup
        .string()
        .required('A segment narration is required.'),
      segment_narration: yup
        .string()
        .required('A segment narration is required.'),
      minutes: yup
        .number()
        .required('Minutes is required.')
        .min(0, 'Minutes must be greater than or equal to 0.')
        .max(60, 'Minutes must be less than or equal to 60.'),
      seconds: yup
        .number()
        .required('Seconds is required.')
        .min(0, 'Seconds must be greater than or equal to 0.')
        .max(60, 'Seconds must be less than or equal to 60.')
    })
  )
});

export const PEDiscussionQuestionSchema = yup.object().shape({
  material_title_en: yup
    .string()
    .required('An english material title is required.'),
  material_title_tl: yup
    .string()
    .required('A target language material title name is required.'),
  link: yup.string().required('A link is required.'),
  sp_url: yup.string(),
  media_material: yup.boolean(),
  media_url: yup.string(),
  material_segment: yup
    .string()
    .required('A portion of the article or video transcript is required.'),
  prompt: yup.string().required('A prompt is required.'),
  correct_feedback: yup.string().required('Correct feedback is required.'),
  incorrect_feedback: yup.string().required('Incorrect feedback is required.')
});

export const PECommunicationFeedbackSchema = yup.object().shape({
  item_feedback: yup.array().of(
    yup.object().shape({
      label: yup.string().required('A label is required.'),
      incomplete_feedback_tl: yup
        .string()
        .required('Incomplete feedback is required.'),
      low_feedback_tl: yup.string().required('Low feedback is required.'),
      mid_feedback_tl: yup.string().required('Mid feedback is required.'),
      high_feedback_tl: yup.string().required('High feedback is required.'),
      incomplete_feedback_en: yup
        .string()
        .required('Incomplete feedback is required.'),
      low_feedback_en: yup.string().required('Low feedback is required.'),
      mid_feedback_en: yup.string().required('Mid feedback is required.'),
      high_feedback_en: yup.string().required('High feedback is required.'),
      dialog_id: yup.string().required('Dialog Page ID is required.')
    })
  )
});

export const PENonImmersiveCommunicationFeedbackSchema = yup.object().shape({
  item_feedback: yup.array().of(
    yup.object().shape({
      label: yup.string().required('A label is required.'),
      text_tl: yup.string().required('Text is required.'),
      incomplete_feedback_tl: yup
        .string()
        .required('Incomplete feedback is required.'),
      low_feedback_tl: yup.string().required('Low feedback is required.'),
      mid_feedback_tl: yup.string().required('Mid feedback is required.'),
      high_feedback_tl: yup.string().required('High feedback is required.'),
      text_en: yup.string().required('Text is required.'),
      incomplete_feedback_en: yup
        .string()
        .required('Incomplete feedback is required.'),
      low_feedback_en: yup.string().required('Low feedback is required.'),
      mid_feedback_en: yup.string().required('Mid feedback is required.'),
      high_feedback_en: yup.string().required('High feedback is required.')
    })
  )
});

export const JoyrideSchema = yup.object().shape({
  cards: yup.array().of(
    yup.object().shape({
      img: yup.object().shape({
        media_url: yup
          .string()
          .required('Please add the base-media-repo url of the image.'),
        title: yup.string().required('An image title is required.'),
        description: yup.string().required('An image description is required.'),
        link: yup
          .string()
          .required('Please add the link to source (base-media-repo).'),
        published_date: yup
          .string()
          .required('Please add the date image was published.'),
        downloaded_date: yup
          .string()
          .required('Please add the date image was downloaded/referenced.'),
        author: yup.string().required('Please add the author of the source.'),
        cc_license: yup
          .string()
          .required('Please add the cc license of the source.')
      }),
      title: yup.string().required('A card title is required.'),
      description: yup.string().required('A card description is required.'),
      tour: yup.string().required('A tour is required.'),
      steps_img: yup.string(),
      steps: yup.array().of(
        yup.object().shape({
          title: yup.string().required('A step title is required.'),
          content: yup.string().required('Step content is required.'),
          target: yup.string().required('A DOM target is required.'),
          placement: yup.string().required('please  select a placement option.')
        })
      )
    })
  )
});
